import { useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Search } from "lucide-react";
import { ORDER_CATEGORY_LABEL, ORDER_STATUS_LABEL, fmtDate } from "@/lib/format";
import { DocExpiryBadge } from "@/components/StatusBadge";

export default function History() {
  const [plate, setPlate] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const search = async (e) => {
    e.preventDefault();
    setError(""); setData(null);
    try {
      const r = await api.get(`/vehicles/by-plate/${plate.toUpperCase()}/history`);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "No encontrado");
    }
  };

  return (
    <div data-testid="history-page">
      <PageHeader
        tag="Historial Clínico"
        title="Búsqueda por Patente"
        description="Consulta el historial técnico completo de un vehículo: mantenciones, reparaciones e inspecciones."
      />

      <form onSubmit={search} className="mb-8 flex gap-3 max-w-xl">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={plate}
            onChange={(e) => setPlate(e.target.value)}
            placeholder="Ej: ABCD-12"
            className="w-full h-touch pl-12 pr-4 border border-gray-300 focus:outline-none focus:border-black font-mono uppercase"
            data-testid="history-plate-input"
          />
        </div>
        <button type="submit" className="h-touch px-6 bg-black text-white text-sm font-bold uppercase tracking-wider hover:bg-blue-600" data-testid="history-search-btn">
          Buscar
        </button>
      </form>

      {error && <div className="status-danger p-4 text-sm" data-testid="history-error">{error}</div>}

      {data && (
        <div data-testid="history-results">
          <div className="border border-gray-200 p-6 mb-6 grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="veta-tag">Patente</p>
              <p className="font-display text-3xl mt-1 font-mono">{data.vehicle.plate}</p>
            </div>
            <div>
              <p className="veta-tag">Vehículo</p>
              <p className="font-display text-xl mt-1">{data.vehicle.brand} {data.vehicle.model}</p>
            </div>
            <div>
              <p className="veta-tag">Año</p>
              <p className="font-display text-xl mt-1">{data.vehicle.year}</p>
            </div>
            <div>
              <p className="veta-tag">Kilometraje</p>
              <p className="font-display text-xl mt-1 font-mono">{(data.vehicle.mileage || 0).toLocaleString("es-CL")}</p>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-px bg-gray-200 border border-gray-200">
            <div className="bg-white p-6">
              <p className="veta-tag mb-4">Documentos</p>
              <div className="divide-y divide-gray-200">
                {data.documents.length === 0 && <p className="text-sm text-gray-500 py-4">Sin documentos.</p>}
                {data.documents.map((d) => (
                  <div key={d.id} className="py-3 flex justify-between items-center gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-bold">{d.doc_type}</p>
                      <p className="text-xs text-gray-500">Vence: {fmtDate(d.expiry_date)}</p>
                    </div>
                    <DocExpiryBadge expiryDate={d.expiry_date} />
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white p-6">
              <p className="veta-tag mb-4">Órdenes de Servicio</p>
              <div className="divide-y divide-gray-200">
                {data.service_orders.length === 0 && <p className="text-sm text-gray-500 py-4">Sin órdenes registradas.</p>}
                {data.service_orders.map((o) => (
                  <div key={o.id} className="py-3">
                    <p className="text-sm font-bold">{ORDER_CATEGORY_LABEL[o.category]}</p>
                    <p className="text-xs text-gray-500 line-clamp-2">{o.description}</p>
                    <p className="text-xs text-gray-400 mt-1 font-mono">
                      {fmtDate(o.created_at)} · {(o.mileage || 0).toLocaleString("es-CL")} km · {ORDER_STATUS_LABEL[o.status]}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
